/**
 * Manager Events Section Component
 * Handles events management for managers
 */
import React from 'react';
import { CalendarDays, Plus, Pencil, Trash2, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { formatDateTime } from './managerConstants';

export function ManagerEventsSection({
    events,
    onOpenNewModal,
    onEdit,
    onDelete,
    isResidential
}) {
    const buttonClass = isResidential ? 'bg-stone-600 hover:bg-stone-700' : 'bg-emerald-600 hover:bg-emerald-700';
    
    return (
        <div className="space-y-6" data-testid="manager-events">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">
                    {isResidential ? 'Activities & Events' : 'Events Management'}
                </h2>
                <Button onClick={onOpenNewModal} className={buttonClass} data-testid="manager-add-event-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Event
                </Button>
            </div>

            <div className="text-sm text-muted-foreground">
                {events.length} events total
            </div>
            
            <div className="space-y-3">
                {events.map(event => (
                    <div key={event.id} className="card-base" data-testid={`manager-event-card-${event.id}`}>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex gap-4 flex-1">
                                {event.image_url && (
                                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                                        <img 
                                            src={event.image_url} 
                                            alt={event.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                            event.category === 'dining' ? 'bg-amber-100 text-amber-700' :
                                            event.category === 'workshop' ? 'bg-purple-100 text-purple-700' :
                                            event.category === 'social' ? 'bg-blue-100 text-blue-700' :
                                            event.category === 'special' ? 'bg-pink-100 text-pink-700' :
                                            event.category === 'wellness' ? 'bg-teal-100 text-teal-700' :
                                            event.category === 'fitness' ? 'bg-orange-100 text-orange-700' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                            {event.category || 'General'}
                                        </span>
                                        {event.is_recurring && (
                                            <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                                isResidential ? 'bg-stone-100 text-stone-700' : 'bg-emerald-100 text-emerald-700'
                                            }`}>
                                                Recurring
                                            </span>
                                        )}
                                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                                            {event.price || 'Free'}
                                        </span>
                                    </div>
                                    <p className="font-bold text-lg">{event.title}</p>
                                    <p className="text-sm text-muted-foreground line-clamp-1">{event.description}</p>
                                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                                        <span><Clock className="w-3 h-3 inline mr-1" />{formatDateTime(event.date_time)}</span>
                                        <span>{event.location}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => onEdit(event)}
                                    data-testid={`manager-edit-event-${event.id}`}
                                    title="Edit Event"
                                >
                                    <Pencil className="w-4 h-4" />
                                </Button>
                                <Button 
                                    variant="destructive" 
                                    size="sm" 
                                    onClick={() => onDelete(event)}
                                    data-testid={`manager-delete-event-${event.id}`}
                                    title="Delete Event"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
                
                {events.length === 0 && (
                    <div className="card-base text-center py-12">
                        <CalendarDays className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No events yet</p>
                        <Button onClick={onOpenNewModal} className={`mt-4 ${buttonClass}`}>
                            Create First Event
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
